import { useEffect, useRef, type CSSProperties } from 'react';
import geometricPatternUrl from '../assets/geometric-pattern.webp';

/**
 * Pre-inverted, flattened tile (black L-lines on opaque white), cropped to the
 * pattern's true 1488×850 repeat so it tiles without seams.
 */
export const GEOMETRIC_PATTERN_URL = geometricPatternUrl;

export const GEOMETRIC_PATTERN_SIZE = '744px 425px';

/** Fraction of scroll speed the pattern follows; lower = stronger parallax. */
const PARALLAX_FACTOR = 0.4;

const patternStyle: CSSProperties = {
  backgroundColor: '#fff',
  backgroundImage: `url(${GEOMETRIC_PATTERN_URL})`,
  backgroundRepeat: 'repeat',
  backgroundSize: GEOMETRIC_PATTERN_SIZE,
  backgroundPosition: 'left top',
  willChange: 'background-position',
};

const washStyle: CSSProperties = {
  backgroundColor: 'rgb(255 255 255 / 94%)',
};

/**
 * Tile origin is pinned to page coordinates (offset by scroll), so stacked
 * sections using this background show one continuous pattern.
 */
export function GeometricPatternBackground() {
  const patternRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = patternRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const factor = reduceMotion ? 0 : PARALLAX_FACTOR;
    let frame = 0;

    const update = () => {
      frame = 0;
      const scrollY = window.scrollY;
      const pageTop = el.getBoundingClientRect().top + scrollY;
      el.style.backgroundPosition = `left ${scrollY * factor - pageTop}px`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      <div ref={patternRef} aria-hidden="true" className="pointer-events-none absolute inset-0" style={patternStyle} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={washStyle} />
    </>
  );
}
